/**
 * PM Portal — Route Scope Matrix
 * Mirrors the BaaS scope matrix. Every /pm/* route must be listed here
 * with its authorization level and (optionally) the permission required.
 *
 * Statuses:
 *   'authorized'   — route is active and accessible to anyone with the listed permission
 *   'read_only'    — accessible but no write mutations permitted (UI hint only)
 *   'access_denied' — route not yet authorized; renders PMAccessDenied screen
 */

export type PMRouteStatus = 'authorized' | 'read_only' | 'access_denied';

export interface PMRouteScope {
  path: string;
  label: string;
  status: PMRouteStatus;
  capability: 'read' | 'read+write';
  /** Permission key required to render the route (checked by hasPermission) */
  permission?: string;
  notes?: string;
}

export const PM_ROUTE_SCOPES: PMRouteScope[] = [
  { path: '/pm', label: 'Dashboard', status: 'authorized', capability: 'read', permission: 'pm.dashboard.view' },
  { path: '/pm/command-center', label: 'Command Center', status: 'authorized', capability: 'read', permission: 'pm.dashboard.view' },
  { path: '/pm/end-users', label: 'End Users', status: 'authorized', capability: 'read', permission: 'pm.end_users.view' },
  { path: '/pm/onboarding-status', label: 'Onboarding Status', status: 'authorized', capability: 'read', permission: 'pm.end_users.view' },
  { path: '/pm/cards', label: 'Cards', status: 'authorized', capability: 'read+write', permission: 'pm.cards.view' },
  { path: '/pm/orders', label: 'Orders', status: 'authorized', capability: 'read+write', permission: 'pm.orders.view' },
  { path: '/pm/transactions', label: 'Transactions', status: 'authorized', capability: 'read', permission: 'pm.transactions.view' },
  { path: '/pm/disputes', label: 'Disputes', status: 'authorized', capability: 'read+write', permission: 'pm.disputes.view' },
  { path: '/pm/fraud', label: 'Fraud & Monitoring', status: 'authorized', capability: 'read', permission: 'pm.fraud.view' },
  { path: '/pm/controls', label: 'Controls', status: 'authorized', capability: 'read+write', permission: 'pm.cards.controls.manage' },
  { path: '/pm/requests', label: 'Change Requests', status: 'authorized', capability: 'read+write', permission: 'pm.controls.request' },
  { path: '/pm/reports', label: 'Reports', status: 'authorized', capability: 'read', permission: 'pm.reports.view' },
  { path: '/pm/analytics', label: 'Analytics', status: 'authorized', capability: 'read', permission: 'pm.reports.view' },
  { path: '/pm/scheduled-reports', label: 'Scheduled Reports', status: 'authorized', capability: 'read+write', permission: 'pm.reports.view' },
  { path: '/pm/compliance', label: 'Compliance', status: 'authorized', capability: 'read', permission: 'pm.compliance.view' },
  { path: '/pm/team', label: 'Team', status: 'authorized', capability: 'read+write', permission: 'pm.team.view' },
  { path: '/pm/api', label: 'API', status: 'authorized', capability: 'read+write', permission: 'pm.api.view' },
  { path: '/pm/settlement', label: 'Settlement', status: 'authorized', capability: 'read', permission: 'pm.billing.view' },
  { path: '/pm/billing', label: 'Billing', status: 'authorized', capability: 'read', permission: 'pm.billing.view' },
  { path: '/pm/statements', label: 'Statements', status: 'authorized', capability: 'read', permission: 'pm.billing.view' },
  { path: '/pm/audit', label: 'Audit Log', status: 'authorized', capability: 'read', permission: 'pm.audit.view' },
  { path: '/pm/approvals', label: 'Approvals', status: 'authorized', capability: 'read+write', permission: 'pm.approvals.view' },
  { path: '/pm/settings', label: 'Settings', status: 'authorized', capability: 'read+write', permission: 'pm.settings.view' },
  { path: '/pm/settings/branding', label: 'Branding', status: 'authorized', capability: 'read+write', permission: 'pm.settings.view' },
  { path: '/pm/settings/webhooks', label: 'Webhooks', status: 'authorized', capability: 'read+write', permission: 'pm.api.view' },
];

export function getPMRouteScope(path: string): PMRouteScope | undefined {
  // Match longest path first so /pm/settings/webhooks wins over /pm/settings
  const sorted = [...PM_ROUTE_SCOPES].sort((a, b) => b.path.length - a.path.length);
  return sorted.find(r =>
    r.path === path ||
    // Allow nested detail routes (e.g. /pm/end-users/abc) to inherit parent scope
    (r.path !== '/pm' && path.startsWith(r.path + '/')),
  );
}

export function isPMRouteAuthorized(path: string): boolean {
  const scope = getPMRouteScope(path);
  if (!scope) return true; // Unmapped routes (e.g. /pm/access-denied, detail pages) — let layout decide
  return scope.status !== 'access_denied';
}
